import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip,
  Checkbox,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Language as LanguageIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { DownloadedWebpage } from '../types/index';

interface DownloadedWebpagesListProps {
  downloadedWebpages: DownloadedWebpage[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onDelete: (id: string) => void;
  onRefresh: () => void;
  onRefreshQnaPairs?: () => void;
  loading?: boolean;
  error?: string | null;
  showSelection?: boolean; // New prop to control selection display
}

const DownloadedWebpagesList: React.FC<DownloadedWebpagesListProps> = ({
  downloadedWebpages,
  selectedIds,
  onSelectionChange,
  onDelete,
  onRefresh,
  onRefreshQnaPairs,
  loading = false,
  error = null,
  showSelection = true
}) => {
  const [selectAll, setSelectAll] = useState(false);

  const handleDelete = async (id: string) => {
    await onDelete(id);
    // Refresh Q&A pairs list after deleting a webpage (which also deletes associated Q&A pairs)
    if (onRefreshQnaPairs) {
      onRefreshQnaPairs();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <CircularProgress size={20} />;
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing':
        return 'warning';
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getQAPairsDisplay = (webpage: DownloadedWebpage) => {
    if (webpage.status === 'processing') {
      return 'Processing...';
    } else if (webpage.status === 'error') {
      return 'Error';
    } else if (webpage.status === 'completed') {
      const count = webpage.qa_pairs_count || 0;
      return count === 0 ? 'No Q&A pairs' : `${count} Q&A pair${count > 1 ? 's' : ''}`;
    }
    return 'Ready';
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      onSelectionChange(downloadedWebpages.map(webpage => webpage.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleItemSelect = (id: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
      setSelectAll(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getImageInfo = (webpage: DownloadedWebpage) => {
    if (webpage.process_images && webpage.image_data?.totalImages) {
      return `${webpage.image_data.totalImages} images`;
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Downloaded Webpages ({downloadedWebpages.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={onRefresh} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showSelection && downloadedWebpages.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Checkbox
            checked={selectAll}
            onChange={(e) => handleSelectAll(e.target.checked)}
            indeterminate={selectedIds.length > 0 && selectedIds.length < downloadedWebpages.length}
          />
          <Typography component="span" sx={{ ml: 1 }}>
            Select all ({selectedIds.length} selected)
          </Typography>
        </Box>
      )}

      {loading && downloadedWebpages.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : downloadedWebpages.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LanguageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No downloaded webpages yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start by downloading a webpage in the "Download Webpages" tab
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
          <List dense>
            {downloadedWebpages.map((webpage, index) => (
              <React.Fragment key={webpage.id}>
                <ListItem
                  sx={{
                    bgcolor: selectedIds.includes(webpage.id) ? 'action.selected' : 'inherit'
                  }}
                >
                  {showSelection && (
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedIds.includes(webpage.id)}
                        onChange={(e) => handleItemSelect(webpage.id, e.target.checked)}
                        size="small"
                      />
                    </ListItemIcon>
                  )}
                  
                  <ListItemIcon>
                    {getStatusIcon(webpage.status)}
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '600px'
                          }}
                        >
                          {webpage.url}
                        </Typography>
                        <Chip
                          label={getQAPairsDisplay(webpage)}
                          size="small"
                          color={getStatusColor(webpage.status) as any}
                          variant="outlined"
                        />
                        {webpage.process_images && getImageInfo(webpage) && (
                          <Tooltip title={getImageInfo(webpage) || 'Images downloaded'}>
                            <ImageIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <span>
                        {webpage.title && webpage.title.trim() && 
                         !webpage.title.toLowerCase().includes('blocked') && 
                         webpage.title !== webpage.url && (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }} component="span">
                              {webpage.title}
                            </Typography>
                            <br />
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary" component="span">
                          Downloaded: {formatDate(webpage.downloaded_at)}
                        </Typography>
                        {webpage.page_depth !== undefined && webpage.page_depth > 0 && (
                          <>
                            <br />
                            <Typography variant="caption" color="text.secondary" component="span">
                              Crawl depth: {webpage.page_depth}
                            </Typography>
                          </>
                        )}
                        {getImageInfo(webpage) && (
                          <>
                            <br />
                            <Typography variant="caption" color="primary" component="span">
                              {getImageInfo(webpage)}
                            </Typography>
                          </>
                        )}
                      </span>
                    }
                    primaryTypographyProps={{
                      sx: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />

                  <ListItemSecondaryAction>
                    <Tooltip title="Delete downloaded webpage">
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDelete(webpage.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < downloadedWebpages.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {showSelection && selectedIds.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length} webpage{selectedIds.length > 1 ? 's' : ''} selected for Q&A generation
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DownloadedWebpagesList;